(function () {
  'use strict';

  function initMegaMenu(header) {
    var triggers = header.querySelectorAll('[data-mega-trigger]');
    var panels = header.querySelectorAll('[data-mega-handle]');
    if (triggers.length === 0 || panels.length === 0) return;

    var openHandle = null;
    var hoverCloseTimer = null;
    var HOVER_CLOSE_DELAY = 150;

    function open(handle) {
      if (handle === openHandle) return;
      openHandle = handle;
      triggers.forEach(function (trigger) {
        var active = trigger.getAttribute('data-mega-trigger') === handle;
        trigger.setAttribute('aria-expanded', active ? 'true' : 'false');
      });
      panels.forEach(function (panel) {
        var match = panel.getAttribute('data-mega-handle') === handle;
        panel.hidden = !match;
      });
      header.setAttribute('data-mega-open', handle);
    }

    function close() {
      if (!openHandle) return;
      openHandle = null;
      triggers.forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      });
      panels.forEach(function (panel) {
        panel.hidden = true;
      });
      header.removeAttribute('data-mega-open');
    }

    function toggle(handle) {
      if (handle === openHandle) {
        close();
      } else {
        open(handle);
      }
    }

    function scheduleHoverClose() {
      window.clearTimeout(hoverCloseTimer);
      hoverCloseTimer = window.setTimeout(close, HOVER_CLOSE_DELAY);
    }

    function cancelHoverClose() {
      window.clearTimeout(hoverCloseTimer);
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.stopPropagation();
        cancelHoverClose();
        toggle(trigger.getAttribute('data-mega-trigger'));
      });
      trigger.addEventListener('mouseenter', function () {
        cancelHoverClose();
        open(trigger.getAttribute('data-mega-trigger'));
      });
      trigger.addEventListener('mouseleave', scheduleHoverClose);
    });

    panels.forEach(function (panel) {
      panel.addEventListener('mouseenter', cancelHoverClose);
      panel.addEventListener('mouseleave', scheduleHoverClose);
    });

    document.addEventListener('click', function (event) {
      if (!openHandle) return;
      if (header.contains(event.target)) return;
      close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && openHandle) {
        close();
      }
    });
  }

  function initMegaColumns(header) {
    var panels = header.querySelectorAll('[data-mega-columns]');

    panels.forEach(function (panel) {
      var triggers = panel.querySelectorAll('[data-featured-trigger]');
      var cards = panel.querySelectorAll('[data-featured-card]');
      if (triggers.length === 0 || cards.length === 0) return;

      function showCard(handle) {
        var target = panel.querySelector('[data-featured-card="' + handle + '"]');
        if (!target) return;
        cards.forEach(function (card) {
          card.hidden = card !== target;
        });
      }

      triggers.forEach(function (trigger) {
        var handle = trigger.getAttribute('data-featured-trigger');
        trigger.addEventListener('mouseenter', function () {
          showCard(handle);
        });
        trigger.addEventListener('focus', function () {
          showCard(handle);
        });
      });
    });
  }

  // The drawer is `position: fixed`, so what it needs is the header's bottom
  // edge in VIEWPORT coordinates — not the header's own height. Those two are
  // the same number only while nothing sits above the header; once the topbar
  // is in the header group, offsetHeight left the drawer starting one topbar
  // too high and covering the bottom of the header.
  //
  // Reading the rect also means anything else added above the header later
  // (a second announcement bar, a promo strip) is accounted for automatically.
  // Clamped at 0 for the case where the header has scrolled out of view: the
  // header is not sticky, so a negative bottom would pull the drawer off-screen.
  function syncHeaderOffset(header) {
    var rect = header.getBoundingClientRect();
    var offset = rect.height ? Math.max(0, rect.bottom) : header.offsetHeight || 80;
    document.documentElement.style.setProperty('--header-bottom', Math.round(offset) + 'px');
  }

  function initMenuDrawer(header) {
    var drawer = document.querySelector('[data-menu-drawer]');
    if (!drawer) return;

    var toggles = document.querySelectorAll('[data-menu-drawer-toggle]');
    var subTriggers = drawer.querySelectorAll('[data-submenu-trigger]');
    var backButtons = drawer.querySelectorAll('[data-submenu-back]');
    var panels = drawer.querySelectorAll('.menu-drawer__panel');
    var subPanels = drawer.querySelectorAll('.menu-drawer__panel--sub');
    var mainPanel = drawer.querySelector('.menu-drawer__panel--main');

    var savedScroll = 0;
    // Navigation stack of sub-panel elements (deepest last); main is the base.
    var stack = [];

    // Triggers carry the bare handle (e.g. "shop", "shop-col1"); the panel's
    // data-panel is "sub-<handle>". Prepending here keeps the markup compatible
    // with any older cached script that used the same convention.
    function panelForTrigger(value) {
      return drawer.querySelector('.menu-drawer__panel[data-panel="sub-' + value + '"]');
    }

    function renderStack() {
      var active = stack.length ? stack[stack.length - 1] : mainPanel;
      // Only the deepest panel is `is-active`; the CSS hides every other sub
      // (transform + visibility:hidden) so panels can never overlap.
      subPanels.forEach(function (panel) {
        panel.classList.toggle('is-active', panel === active);
      });
      // Keep only the visible panel interactive (out of the tab order / a11y
      // tree otherwise). Main is inert whenever a sub is open.
      panels.forEach(function (panel) {
        panel.inert = panel !== active;
      });
      subTriggers.forEach(function (trigger) {
        var target = panelForTrigger(trigger.getAttribute('data-submenu-trigger'));
        var expanded = !!target && stack.indexOf(target) > -1;
        trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
      if (stack.length) {
        drawer.setAttribute('data-active-sub', '');
      } else {
        drawer.removeAttribute('data-active-sub');
      }
      active.scrollTop = 0;
    }

    // Move keyboard focus into the panel that just became active so the user's
    // place follows the navigation (the outgoing panel becomes inert). Skipped
    // on init/close so it never steals focus unprompted.
    function focusActivePanel() {
      var active = stack.length ? stack[stack.length - 1] : mainPanel;
      var target = active.querySelector('.menu-drawer__back') || active.querySelector('a, button');
      if (target) {
        try {
          target.focus({ preventScroll: true });
        } catch (e) {
          target.focus();
        }
      }
    }

    function pushPanel(triggerValue) {
      var panel = panelForTrigger(triggerValue);
      if (!panel || stack.indexOf(panel) > -1) return;
      stack.push(panel);
      renderStack();
      focusActivePanel();
    }

    function popPanel() {
      if (!stack.length) return;
      stack.pop();
      renderStack();
      focusActivePanel();
    }

    function resetPanels() {
      stack = [];
      renderStack();
    }

    function openDrawer() {
      savedScroll = window.scrollY;
      if (savedScroll > 0) {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
      // Measured AFTER the scroll reset, not before: the offset is now read off
      // the viewport, so the header has to be back at its resting place first.
      // `behavior: 'instant'` settles synchronously, so this reads the new value.
      syncHeaderOffset(header);
      document.documentElement.classList.add('menu-drawer-open');
      drawer.setAttribute('data-open', '');
      drawer.removeAttribute('inert');
      drawer.setAttribute('aria-hidden', 'false');
      toggles.forEach(function (t) {
        t.setAttribute('aria-expanded', 'true');
      });
    }

    function closeDrawer() {
      drawer.removeAttribute('data-open');
      drawer.setAttribute('inert', '');
      drawer.setAttribute('aria-hidden', 'true');
      toggles.forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
      document.documentElement.classList.remove('menu-drawer-open');
      if (savedScroll > 0) {
        window.scrollTo({ top: savedScroll, behavior: 'instant' });
        savedScroll = 0;
      }
      window.setTimeout(resetPanels, 400);
    }

    function toggleDrawer() {
      if (drawer.hasAttribute('data-open')) {
        closeDrawer();
      } else {
        openDrawer();
      }
    }

    toggles.forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        toggleDrawer();
      });
    });

    subTriggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        pushPanel(trigger.getAttribute('data-submenu-trigger'));
      });
    });

    backButtons.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        popPanel();
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (!drawer.hasAttribute('data-open')) return;
      if (stack.length) {
        popPanel();
      } else {
        closeDrawer();
      }
    });

    // Establish the initial panel state (main panel active).
    renderStack();
  }

  function boot() {
    var headers = document.querySelectorAll('[data-header]');
    headers.forEach(initMegaMenu);
    headers.forEach(initMegaColumns);
    if (headers.length > 0) {
      var header = headers[0];
      syncHeaderOffset(header);
      window.addEventListener('resize', function () {
        syncHeaderOffset(header);
      });
      initMenuDrawer(header);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
